import { useGranularPermissionsFlag } from 'app/flags_main/context/FeatureFlag.context';
import { APIResponseWithPermissions } from 'types/frontend';
import { getUserAccountInfo } from 'utils';

export enum EntityType {
  ENVIRONMENT = 'environment',
  FLAG = 'flag',
  GROUP = 'group',
  REPORT = 'report',
  RULE = 'rule',
  RULESET = 'ruleset',
  VARIABLE = 'variable',
  VARIATION = 'variation',
}

const roleLevel: { [role: string]: number } = {
  admin: 4,
  publish: 3,
  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
  edit: 2,
  view: 1,
  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
  none: 0,
};

export function useGranularPermissions(): boolean {
  // granular permissions is enabled only when enabled both in -
  // - Full Plan Management (FPM)
  // - Optimizely Python/JS (dogfood)

  const flagNameInFPM = 'granular-permissions';

  const accountPermissions =
    // @ts-ignore
    window?.optlyConfig?.account_info?.permissions?.accountPermissions || [];
  const isEnabledInFPM = (accountPermissions as string[]).includes(
    flagNameInFPM
  );

  const { enabled: isEnabledInOptimizely } = useGranularPermissionsFlag();
  return isEnabledInFPM && isEnabledInOptimizely;
}

// if entity role is not present even though granular permissions are enabled, we use project-level permissions instead.
export function shouldApplyGranularPermissions(
  isGranularPermissionsEnabled?: boolean,
  item?: APIResponseWithPermissions
) {
  return !!(isGranularPermissionsEnabled && item && item.role);
}

export function canViewEntity(role?: string): boolean {
  if (!role) return false;
  return roleLevel[role] >= roleLevel.view;
}

export function canEditEntity(role?: string): boolean {
  if (!role) return false;
  return roleLevel[role] >= roleLevel.edit;
}

export function canPublishEntity(role?: string): boolean {
  if (!role) return false;
  return roleLevel[role] >= roleLevel.publish;
}

export function canAdminEntity(role?: string): boolean {
  if (!role) return false;
  return roleLevel[role] >= roleLevel.admin;
}

/**
 * This will open the manage-permissions dialog from monolith SPA.
 * The eventListener is defined in the monolith codebase.
 * The dialog itself is loaded again from the PERMISSION-UX SCS.
 */
function openManagePermissions(
  projectId: number,
  entityType: EntityType,
  entityId: number,
  entityKey: string
): void {
  const accountId = getUserAccountInfo().id;

  document.dispatchEvent(
    new CustomEvent('OPTLY:granularPermissions:open', {
      detail: { accountId, entityId, entityKey, entityType, projectId },
    })
  );
}

const GranularPermissions = {
  openManagePermissions,
};

export default GranularPermissions;
